import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Loader2, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadForm = ({ variant = 'default' }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    project_type: '',
    budget_range: '',
    preferred_date: '',
    message: '',
  });
  const [date, setDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const projectTypes = [
    'Living Room Decoration',
    'Accent Wall & Wallpaper Styling',
    'Bedroom Enhancements',
    'Lighting & Decor Consultation',
    'Seasonal Decoration Updates',
    'Full Home Styling',
    'Other',
  ];

  const budgetRanges = [
    'Under $2,000',
    '$2,000 - $5,000',
    '$5,000 - $10,000',
    '$10,000 - $20,000',
    '$20,000+',
    'Not Sure Yet',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (selectedDate) => {
    setDate(selectedDate);
    if (selectedDate) {
      setFormData((prev) => ({
        ...prev,
        preferred_date: format(selectedDate, 'yyyy-MM-dd'),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      setIsSuccess(true);
      toast.success('Consultation request submitted successfully!');
      setFormData({
        full_name: '',
        phone: '',
        email: '',
        project_type: '',
        budget_range: '',
        preferred_date: '',
        message: '',
      });
      setDate(null);
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
      console.error('Error submitting lead:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div
        data-testid="lead-form-success"
        className="text-center py-12 px-6 bg-white rounded-lg"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="font-heading text-2xl mb-3">Thank You!</h3>
        <p className="text-[#6E6E6E] mb-6">
          Your consultation request has been received. Our team will contact you
          within 24 hours.
        </p>
        <Button
          data-testid="submit-another-btn"
          onClick={() => setIsSuccess(false)}
          variant="outline"
          className="border-[#4A4A4A] text-[#4A4A4A]"
        >
          Submit Another Request
        </Button>
      </div>
    );
  }

  return (
    <form
      data-testid="lead-capture-form"
      onSubmit={handleSubmit}
      className={`space-y-6 ${
        variant === 'compact' ? 'bg-white p-6 rounded-lg' : ''
      }`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="form-group">
          <Label className="label-editorial">Full Name *</Label>
          <Input
            data-testid="input-full-name"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            placeholder="John Smith"
            required
            className="input-editorial"
          />
        </div>
        <div className="form-group">
          <Label className="label-editorial">Phone Number *</Label>
          <Input
            data-testid="input-phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+1 (555) 000-0000"
            required
            className="input-editorial"
          />
        </div>
      </div>

      <div className="form-group">
        <Label className="label-editorial">Email Address *</Label>
        <Input
          data-testid="input-email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="john@example.com"
          required
          className="input-editorial"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="form-group">
          <Label className="label-editorial">Project Type *</Label>
          <Select
            value={formData.project_type}
            onValueChange={(value) => handleSelectChange('project_type', value)}
            required
          >
            <SelectTrigger data-testid="select-project-type" className="input-editorial border-b border-gray-300 rounded-none">
              <SelectValue placeholder="Select project type" />
            </SelectTrigger>
            <SelectContent>
              {projectTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="form-group">
          <Label className="label-editorial">Budget Range *</Label>
          <Select
            value={formData.budget_range}
            onValueChange={(value) => handleSelectChange('budget_range', value)}
            required
          >
            <SelectTrigger data-testid="select-budget" className="input-editorial border-b border-gray-300 rounded-none">
              <SelectValue placeholder="Select budget range" />
            </SelectTrigger>
            <SelectContent>
              {budgetRanges.map((range) => (
                <SelectItem key={range} value={range}>
                  {range}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="form-group">
        <Label className="label-editorial">Preferred Consultation Date</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              data-testid="date-picker-trigger"
              variant="outline"
              className={`w-full justify-start text-left font-normal input-editorial border-b border-gray-300 rounded-none ${
                !date && 'text-gray-400'
              }`}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? format(date, 'PPP') : 'Pick a date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateChange}
              disabled={(date) => date < new Date()}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="form-group">
        <Label className="label-editorial">Additional Details (Optional)</Label>
        <Textarea
          data-testid="input-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us about your project vision..."
          rows={4}
          className="input-editorial resize-none"
        />
      </div>

      <Button
        data-testid="submit-consultation-btn"
        type="submit"
        disabled={isSubmitting}
        className="btn-accent w-full md:w-auto"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          'Request My Consultation'
        )}
      </Button>

      <p className="text-xs text-[#6E6E6E] mt-4">
        We respect your privacy. Your information will only be used to respond
        to your inquiry.
      </p>
    </form>
  );
};

export default LeadForm;
